import React from 'react';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { LayoutComposer, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './TransactionProcessBuyerPage.module.css';

const layoutAreas = `
  topbar
  main
  footer
`;

function buildHowToSchema(rootURL) {
  return {
    '@type': 'HowTo',
    name: 'How to Buy Used Skydiving Gear on Avento',
    url: `${rootURL}/how-to-buy`,
    description:
      'Step-by-step buyer process on Avento: choose shipping or pickup, pay into escrow, receive the gear, inspect with a rigger, and either approve or dispute the order.',
    totalTime: 'P28D',
    tool: [
      {
        '@type': 'HowToTool',
        name: 'Certified skydiving rigger for gear inspection',
      },
    ],
    supply: [
      { '@type': 'HowToSupply', name: 'Listing page of the gear on Avento' },
      { '@type': 'HowToSupply', name: 'Credit or debit card for escrow payment' },
    ],
    step: [
      {
        '@type': 'HowToStep',
        name: 'Choose shipping region or local pickup',
        text:
          'When ordering, select the correct shipping region (National, Continental, Global) based on the seller location, or arrange a local pickup.',
      },
      {
        '@type': 'HowToStep',
        name: 'Pay into escrow',
        text:
          'Your payment is held securely in the Avento escrow account and is not released to the seller until the inspection period closes without a dispute.',
      },
      {
        '@type': 'HowToStep',
        name: 'Receive the gear',
        text:
          'For shipping the seller has 7 days to dispatch the order. For pickup you have 14 days to collect the item.',
      },
      {
        '@type': 'HowToStep',
        name: 'Inspect with a rigger',
        text:
          'You have 28 days after shipment (14 days delivery window + 14 days inspection) or 14 days after pickup to have the gear inspected by your rigger.',
      },
      {
        '@type': 'HowToStep',
        name: 'Approve or dispute',
        text:
          'If everything matches the listing, mark the order as inspected to release payout. If the gear is not as described, open a dispute within the inspection window for refund or return.',
      },
    ],
    about: {
      '@type': 'Thing',
      name: 'Buying used skydiving gear (rigs, canopies, reserves, AADs, helmets)',
    },
    inLanguage: 'en',
  };
}

function buildFaqSchema() {
  return {
    '@type': 'FAQPage',
    name: 'Avento Buyer Process — Common Questions',
    description:
      'Frequently asked questions about the Avento buyer process: shipping, pickup, inspection windows, and disputes.',
    inLanguage: 'en',
    mainEntity: [
        {
          '@type': 'Question',
          name: 'What shipping regions are available?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'If you chose shipping, you have to choose the shipping region (National, Continental, Global). Make sure that you choose the right shipping region by comparing the sellers\' location with your shipping destination.',
          },
        },
        {
          '@type': 'Question',
          name: 'How long does the seller have to ship?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The seller has 7 days to ship the item. If they don\'t ship within 7 days, the order is automatically canceled, and you will receive a full refund.',
          },
        },
        {
          '@type': 'Question',
          name: 'Who bears the risk during shipping?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The buyer generally bears the risk during shipping. However, the seller must follow responsibilities in shipping like using a reliable carrier, tracked shipping, and adequate packaging.',
          },
        },
        {
          '@type': 'Question',
          name: 'How long do I have to inspect the item after shipping?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'After the seller has shipped the item, you have 28 days to receive and inspect the item (14 days expected shipping + 14 days for rigger inspection).',
          },
        },
        {
          '@type': 'Question',
          name: 'What if the item does not match the listing?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Dispute the order on Avento. We will evaluate the situation. If confirmed, the order is canceled, you ship back the gear and receive a refund.',
          },
        },
        {
          '@type': 'Question',
          name: 'How long do I have to pick up for local pickup?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'You have 14 days to pick up the item from the seller.',
          },
        },
        {
          '@type': 'Question',
          name: 'How long do I have to inspect after pickup?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '14 days to have the item inspected by your rigger. Mark the order as checked on Avento, then the seller is paid out.',
          },
        },
      {
        '@type': 'Question',
        name: 'How long does it take to resolve a dispute?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We try to resolve disputes as fast as possible but maximally within 30 days. In extremely complex cases, it might take up to 50 days.',
        },
      },
    ],
  };
}

const TransactionProcessBuyerPageComponent = props => {
  const { scrollingDisabled } = props;
  const config = useConfiguration();

  const rootURL = config.marketplaceRootURL || '';
  const schemaTitle = 'Buying Process on Avento';
  const schemaDescription =
    'Step-by-step guide of the buying process on Avento. Shipping times, pickup, inspection periods and dispute on Avento. Buy used skydiving gear safely.';
  const howToSchema = buildHowToSchema(rootURL);
  const faqSchema = buildFaqSchema();

  return (
    <Page
      title={schemaTitle}
      description={schemaDescription}
      schema={[howToSchema, faqSchema]}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="TransactionProcessBuyerPage" />
              </Topbar>

              <Main as="main" className={css.main}>
                {/* Page header */}
                <div className={css.pageHeader}>
                  <div className={css.container}>
                    <h1 className={css.pageTitle}>
                      Shipping and Pickup as a Buyer
                    </h1>
                    <p className={css.pageSubtitle}>
                      The most commonly asked question to shipping and pickup.
                    </p>
                  </div>
                </div>

                {/* Shipping */}
                <section className={css.section}>
                  <div className={css.container}>
                    <h2 className={css.sectionTitle}>Shipping</h2>

                    <h3 className={css.subHeading}>Shipping Region</h3>
                    <p className={css.paragraph}>
                      If you chose shipping, you have to choose the shipping
                      region (National, Continental, Global). Make sure that you
                      choose the right shipping region by comparing the sellers'
                      location with your shipping destination. If you choose the
                      wrong shipping region, you may be asked to pay the
                      difference in shipping cost.
                    </p>

                    <h3 className={css.subHeading}>Shipping Time</h3>
                    <p className={css.paragraph}>
                      After you have purchased the item, your money stays in a
                      secure account. The seller has 7 days time to ship the
                      item. If he or she doesn't ship within 7 days, the order
                      is automatically canceled, and you will receive a full
                      refund.
                    </p>

                    <h3 className={css.subHeading}>Shipping Risk</h3>
                    <p className={css.paragraph}>
                      You as a buyer generally bear the risk during shipping.
                      However, the seller must follow responsibilities in
                      shipping like using a reliable carrier, tracked shipping
                      and adequate packaging. If the seller does not follow the
                      guidelines, he may be responsible.
                    </p>

                    <h3 className={css.subHeading}>Inspection</h3>
                    <p className={css.paragraph}>
                      After the seller has shipped the item, you have 28 days
                      time to receive and inspect the item. We plan for a
                      maximum shipping time of 14 days so that you have at least
                      14 days left to have the item inspected by your rigger. If
                      the item does not arrive within 14 days, you can dispute
                      the order on Avento under Avento &gt; Inbox &gt; As a
                      customer. We will then evaluate the situation and make sure
                      that you have enough time for inspection. Note that only
                      disputes over Avento are valid, and a message to us
                      directly might not be considered.
                    </p>
                    <p className={css.paragraph}>
                      After the 28 days, the order is automatically completed as
                      successful.
                    </p>

                    <h3 className={css.subHeading}>Dispute</h3>
                    <p className={css.paragraph}>
                      In case that the listing does not correspond to the listing
                      (Not jumpworthy although stated, broken etc.), dispute the
                      order on Avento. We will evaluate and resolve the
                      situation. When we confirm your claim, the order is
                      canceled, you ship back the gear and will receive a refund.
                      You are responsible for the shipping cost in that case. We
                      may ask you to give us the contact of your rigger to
                      confirm the problem with the gear. Note that only disputes
                      over Avento are valid, and a message to us directly might
                      not be considered.
                    </p>
                    <p className={css.paragraph}>
                      Keep in mind that we transactions are between private
                      individuals, and there is no general right to send back
                      gear for other reasons than material errors (E.g. The
                      harness not fitting, although the information provided was
                      correct, is not a valid reason for a return).
                    </p>
                    <p className={css.paragraph}>
                      We try to resolve disputes as fast as possible but
                      maximally within 30 days. In extremely complex cases, it
                      might take up to 50 days. If one party does not cooperate
                      in resolving the issue by not responding for a long time or
                      other reasons, we may resolve the dispute in favour of the
                      other party.
                    </p>
                  </div>
                </section>

                {/* Pickup */}
                <section className={`${css.section} ${css.lastSection}`}>
                  <div className={css.container}>
                    <h2 className={css.sectionTitle}>Pickup</h2>

                    <h3 className={css.subHeading}>Pickup Time</h3>
                    <p className={css.paragraph}>
                      After you have purchased the item, your money stays in a
                      secure account. You then have 14 days to pick up the item
                      from the seller. If you don't pick up the item and mark it
                      as picked up under Avento &gt; Inbox &gt; As a customer,
                      the transaction will automatically be completed as
                      successful.
                    </p>

                    <h3 className={css.subHeading}>Inspection</h3>
                    <p className={css.paragraph}>
                      After picking up the item, you have 14 days to have your
                      order inspected by your rigger. After successful
                      inspection, please mark the order as checked on Avento.
                      Only then is the seller paid out. If you don't mark it as
                      checked or disputed within 14 days, the transaction is
                      completed automatically, and the seller is paid out.
                    </p>

                    <h3 className={css.subHeading}>Dispute</h3>
                    <p className={css.paragraph}>
                      In case that the listing does not correspond to the listing
                      (Not jumpworthy although stated, broken etc.), dispute the
                      order on Avento. We will evaluate and resolve the
                      situation. When we confirm your claim, the order is
                      canceled, you ship back the gear and will receive a refund.
                      You are responsible for the shipping cost in that case. We
                      may ask you to give us the contact of your rigger to
                      confirm the problem with the gear. Note that only disputes
                      over Avento are valid, and a message to us directly might
                      not be considered.
                    </p>
                    <p className={css.paragraph}>
                      Keep in mind that we transactions are between private
                      individuals, and there is no general right to send back
                      gear for other reasons than material errors (E.g. The
                      harness not fitting, although the information provided was
                      correct, is not a valid reason for a return).
                    </p>
                    <p className={css.paragraph}>
                      We try to resolve disputes as fast as possible but
                      maximally within 30 days. In extremely complex cases, it
                      might take up to 50 days. We may resolve the dispute in
                      favour of the other party.
                    </p>
                  </div>
                </section>
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const TransactionProcessBuyerPage = connect(mapStateToProps)(
  TransactionProcessBuyerPageComponent
);

export default TransactionProcessBuyerPage;
